import React, { useCallback, useEffect, useState } from 'react';
import { Card, Col, Row, Spinner as ReactSpinner } from 'react-bootstrap';
import { useParams, Link } from 'react-router';
import { monitoringAlertInternalApi, transactionApi } from '../apis';
import TransactionAttributes from '../transaction/TransactionAttributes';
import { useSelector } from 'react-redux';
import { AppState } from '../store/store';
import { useQueryAttributeMappings } from '../schema/mappings/mappingsActions';
import Alerts from '../aml/sections/Alerts';
import PersonAttributes from '../aml/sections/PersonAttributes';
import { PersonLink } from '../common/PersonLink';
import { isCounterparty, lengthOrZero } from '../common/helper';
import {
  InternalApiAlertType,
  InternalApiCaseAttachmentType,
  InternalApiEntityType,
  InternalApiMonitoringAlert,
  InternalApiTransaction,
  InternalApiUniversalDocumentEntityType,
} from '../api-internal';
import LinksToRelatedPersons from '../transaction/LinksToRelatedPersons';
import RFISending from '../rfi-sending/RFISending';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import useRfiData from '../rfi-sending/useRfiData';
import RFIAttributes from '../rfi-sending/RFIAttributes';
import { AlertManagement } from './management/AlertManagement';
import { useAlertFlow } from './flow/useAlertFlow';
import { getUserId } from '../auth/authReducer';
import { useMe } from '../organisation/useMe';
import SalvPage from '../common/SalvPage';
import { Documents } from '../common/documents/Documents';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';
import { SalvBanner, SalvBannerAccent, SalvBannerType, SalvProgressCircle } from '@salvapp/ui-components';
import AlertTransactions from './AlertTransactions';
import Has from '../auth/Has';
import { CAN_READ_CASE } from '../auth/permissions';
import AttachmentCases from '../case/attachments/AttachmentCases';

const SCENARIOS_WITH_TRANSACTIONS_LIMIT = 12;

export const MonitoringAlert = () => {
  const [fetching, setFetching] = useState(true);
  const [alert, setAlert] = useState<InternalApiMonitoringAlert | null>(null);
  const [transaction, setTransaction] = useState<InternalApiTransaction>();
  const { features, currentOrganisation } = useMe();

  const activeUserId = useSelector((state: AppState) => getUserId(state));
  const alertId = Number(useParams<{ id: string }>().id);

  const [setDirty, resetBlocker, renderNavigationButtons] = useAlertFlow(
    alert?.assignee?.id == activeUserId,
    alertId,
    InternalApiAlertType.MONITORING
  );

  const initialFetch = useCallback(async () => {
    resetBlocker();
    setFetching(true);
    try {
      const alert = await monitoringAlertInternalApi.getAlertInternal({
        alertId: alertId,
      });
      setAlert(alert);
    } catch (_: unknown) {
      setAlert(null);
      return;
    } finally {
      setFetching(false);
    }

    setFetching(false);
  }, [alertId]);

  useEffect(() => {
    initialFetch();
  }, [alertId, initialFetch]);

  useEffect(() => {
    if (!alert?.transaction?.id) return;

    fetchTransaction(alert.transaction.id);
  }, [alert]);

  const fetchTransaction = async (transactionId: string) => {
    const transaction = await transactionApi.findTransactionById({
      transactionId,
    });
    setTransaction(transaction);
  };

  const scenarioEntityType = (alert: InternalApiMonitoringAlert) => {
    if (isCounterparty(alert.person)) {
      return '';
    }
    return alert.transaction ? 'TRANSACTION ' : 'PERSON ';
  };

  const alertColumns = [
    { key: 'ID', sortable: false },
    { key: 'Date', sortable: false },
    { key: 'Details', sortable: false },
    { key: 'Actions', sortable: false },
  ];

  const isRfiEnabled = isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_OUTGOING, features);
  const rfiData = useRfiData(alertId, InternalApiAlertType.MONITORING, isRfiEnabled);

  const {
    data: attributeMappings,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
  } = useQueryAttributeMappings();

  if (isAttributeMappingsError) {
    return <DataLoadingErrorFallback message={'Could not load Attribute Mappings'} />;
  } else if (isAttributeMappingsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Attribute Mappings'} />;
  }

  return (
    <SalvPage
      title={alert ? `${scenarioEntityType(alert)} MONITORING Alert ${alertId}` : ''}
      actions={renderNavigationButtons()}
      subTitle={
        <>
          {(!alert?.transaction || (transaction && lengthOrZero(transaction.relatedPersons) < 2)) && (
            <div style={{ display: 'flex', gap: '24px' }}>
              {alert && (
                <div>
                  <span className='text-secondary m-r-4'>
                    {isCounterparty(alert.person) ? 'Counterparty:' : 'Customer:'}
                  </span>
                  <PersonLink person={alert.person} mappings={attributeMappings.map} />
                </div>
              )}
              {alert?.transaction && (
                <div>
                  <span className='text-secondary m-r-4'>Transaction:</span>
                  <Link to={`/transactions/${alert.transaction.id}`}>{alert.transaction.id}</Link>
                </div>
              )}
            </div>
          )}
          {transaction && lengthOrZero(transaction.relatedPersons) >= 2 && (
            <LinksToRelatedPersons
              transaction={transaction}
              currency={currentOrganisation?.currency}
              includeLinkToTrx={true}
            />
          )}
        </>
      }
    >
      {fetching ? (
        <Card>
          <Card.Body>
            <div className='small'>Loading...</div>
            <ReactSpinner className='loader-spinner' animation='border' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </ReactSpinner>
          </Card.Body>
        </Card>
      ) : alert !== null && alert?.id === alertId ? (
        <>
          <Row>
            <Col md={4} className='sticky-col' style={{ maxHeight: 'none' }}>
              {alert.transaction && (
                <TransactionAttributes
                  transaction={alert.transaction}
                  showLinkToTransactionPage={false}
                  showLinkToCustomerPage={false}
                />
              )}
              {!alert.transaction && <PersonAttributes person={alert.person} />}
              {isRfiEnabled && <RFIAttributes rfiData={rfiData} />}
            </Col>
            <Col md={8}>
              <AlertManagement
                entityType={alert.transaction ? InternalApiEntityType.TRANSACTION : InternalApiEntityType.PERSON}
                alertId={alert.id}
                alertSubType={alert.score?.[0]?.scenarioType}
                alertType={InternalApiAlertType.MONITORING}
                postAlertPropertyChange={setDirty}
              />
              {isRfiEnabled && (
                <RFISending
                  alert={alert}
                  alertType={InternalApiAlertType.MONITORING}
                  rfiData={rfiData}
                  onChange={() => initialFetch()}
                />
              )}
              <Alerts
                personId={alert.person.id}
                page={0}
                size={20}
                total={1}
                loading={fetching}
                alerts={[alert]}
                attributes={alertColumns}
                onUpdate={() => Promise.resolve()}
                compact={true}
                title='Alert details'
              />
              <Documents
                className='universal-documents--legacy'
                entityType={InternalApiUniversalDocumentEntityType.MONITORING_ALERT}
                entityId={alertId}
              />
              {isFeatureEnabled(ORGANISATION_FEATURE.CASE_MANAGEMENT, features) && (
                <Has allPermissions={[CAN_READ_CASE]}>
                  <AttachmentCases
                    attachmentType={InternalApiCaseAttachmentType.MONITORING_ALERT}
                    externalId={String(alertId)}
                  />
                </Has>
              )}
            </Col>
          </Row>
          <Row className={'alert-transactions'}>
            {alert.score.map(
              (score, i) =>
                i < SCENARIOS_WITH_TRANSACTIONS_LIMIT && (
                  <Col key={i} md={12} data-testid='alert-transactions'>
                    <AlertTransactions score={score} defaultExpanded={alert.score.length === 1} />
                  </Col>
                )
            )}
            {alert.score.length > SCENARIOS_WITH_TRANSACTIONS_LIMIT && (
              <Col>
                <SalvBanner
                  accent={SalvBannerAccent.STRONG}
                  message={`Salv has limited the transactions you are seeing to the first ${SCENARIOS_WITH_TRANSACTIONS_LIMIT} triggered scenarios.`}
                  type={SalvBannerType.WARNING}
                  className={'m-b-24'}
                ></SalvBanner>
              </Col>
            )}
          </Row>
        </>
      ) : (
        <Card>
          <Card.Body>
            <div className='small'>Could not find alert with ID: {alertId}.</div>
          </Card.Body>
        </Card>
      )}
    </SalvPage>
  );
};

export default MonitoringAlert;
